import { css } from "@linaria/core";
import cx from "classnames";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import Text from "@ui/Text";
import { colors, shadows } from "@ui/Theme";
import t from "../../../../../../../../i18n";

const AxisZoomTooltipHintContainer = css`
  --offset: 14px;
  --x: 0;
  --y: 0;
  position: fixed;
  top: 0;
  left: 0;
  pointer-events: none;
  opacity: 0;
  transition: opacity 100ms cubic-bezier(0.4, 0, 0.2, 1);
  background-color: ${colors.titanium200};
  color: ${colors.carbon800};
  padding: 4px 8px;
  border-radius: 4px;
  box-shadow: ${shadows.shadow400};
  z-index: 1;
`;

const AxisZoomTooltipHintContainerVisible = css`
  transform: translate(calc(var(--offset) + var(--x)), calc(var(--offset) + var(--y)));
  opacity: 1;
`;

/**
 * @description
 * Impl title html attribute like effect but for svg:g tag (unsupported)
 * with a little bit of design.
 */
export const AxisZoomTooltipHint = memo(({ plot }: { plot: React.RefObject<Highcharts.Chart> }) => {
  const abort = useRef<AbortController>(new AbortController());

  // @ts-ignore
  const timeout = useRef<Timeout>();
  const [visible, show] = useState(false);

  const handlePointer = useCallback(
    (el: HTMLDivElement) => {
      if (!el) return;
      const w = el.clientWidth;
      const axes =
        plot.current?.axes.filter(axis => !axis.options.id?.startsWith("navigator")) ?? [];

      const move = ((e: PointerEvent) => {
        const x = e.clientX > window.innerWidth / 2 ? e.clientX - w - 20 : e.clientX;
        el.style.setProperty("--x", `${x}px`);
        el.style.setProperty("--y", `${e.clientY}px`);
      }) as EventListenerOrEventListenerObject;

      const leave = (e: Event) => {
        e.stopPropagation();
        globalThis.clearTimeout(timeout.current);
        e.target?.removeEventListener("pointermove", move);
        show(false);
      };

      const enter = (e: Event) => {
        e.stopPropagation();
        e.target?.addEventListener("pointermove", move);
        e.target?.addEventListener("pointerleave", leave, { once: true });
        window.addEventListener("scroll", () => show(false), { once: true });
        timeout.current = globalThis.setTimeout(() => show(true), 500);
      };

      for (const axis of axes) {
        // @ts-ignore
        const target = axis.labelGroup.element;
        target.addEventListener("pointerenter", enter, { signal: abort.current.signal });
      }
    },
    [plot],
  );

  useEffect(() => () => abort.current.abort(), []);

  return (
    <div
      className={cx(AxisZoomTooltipHintContainer, {
        [AxisZoomTooltipHintContainerVisible]: visible,
      })}
      ref={handlePointer}
    >
      <Text type="body2">{t("project.dashboard.block.plot.axis_hint")}</Text>
    </div>
  );
});
